import { useState, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { createSlug } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const portfolioItems = [
    {
      id: 2,
      title: "Pomo-Do",
      categories: ["App Design"],
      image: "/pomo_do.webp",
      tags: ["Mobilapp", "Produktivitet"],
      description: "Pyksels eget prosjekt - Pomodoro timer app for produktivitet og mye mer - lanseres snart",
      link: "https://pomo-do.app",
      launchingSoon: true
    },
    {
      id: 7,
      title: "Fersken",
      categories: ["Web Design", "Brand Design", "Digital Marketing"],
      image: "/fersken.webp",
      tags: ["Nettbutikk", "Design", "Reklame", "Takeaway"],
      description: "Design, nettside, nettbutikk, reklame og digital tilstedeværelse for en dagligvare- og takeawaybutikk.",
      link: "https://ferskenfornebu.no"
    },
    {
      id: 6,
      title: "TheAfragrance",
      categories: ["Web Design", "Digital Marketing"],
      image: "/theafragrance.webp",
      tags: ["Nettbutikk", "Optimalisering", "Nyhetsbrev"],
      description: "Overhaling av eksisterende nettbutikk med fokus på design, optimalisering, nyhetsbrev og implementering av Nets-betaling i Shopify.",
      link: "https://theafragrance.no/"
    },
    {
      id: 11,
      title: "Wave",
      categories: ["Web Design", "Brand Design"],
      image: "/wave.webp",
      tags: ["Nettsted", "Logo", "Brand Design"],
      description: "Nettside og logo for et performance marketing byrå med fokus på vekst for klinikker gjennom Clinic Freedom System.",
      link: "https://wav3.no"
    },
    {
      id: 12,
      title: "Smart Shape",
      categories: ["Web Design", "Brand Design"],
      image: "/smartshape.webp",
      tags: ["Nettbutikk", "Logo", "Design"],
      description: "Ny Shopify-nettbutikk for Smart Shape – shapewear skapt for komfort og frihet.",
      link: "https://smartshape.no"
    },
    {
      id: 9,
      title: "Smart Life",
      categories: ["Web Design", "Brand Design"],
      image: "/smartlife.webp",
      tags: ["Nettbutikk", "Branding", "Design"],
      description: "Design og branding for en Shopify nettbutikk som selger smart-produkter.",
      link: "https://smart-life.no/"
    },
    {
      id: 1,
      title: "Telegruppen",
      categories: ["Web Design", "Brand Design", "Digital Marketing"],
      image: "/telegruppen.webp",
      tags: ["Nettsted", "Logo", "Annonser", "Film"],
      description: "Hjemmeside, logo, reklame og filming for Telegruppen.",
      link: "https://telegruppen.no"
    },
    {
      id: 5,
      title: "GSK",
      categories: ["Web Design", "Brand Design"],
      image: "/api/placeholder/600/400",
      tags: ["Nettsted", "Logo", "Fotball"],
      description: "Logo og nettside for et nytt etablert fotballag.",
      launchingSoon: true
    },
    {
      id: 10,
      title: "Nordic Perfumery",
      categories: ["Web Design", "Brand Design"],
      image: "/api/placeholder/600/400",
      tags: ["Nettbutikk", "Branding", "Design"],
      description: "Design, branding og utvikling av Shopify-nettbutikk.",
      launchingSoon: true
    }
  ];

const categories = ["Alle", "Web Design", "App Design", "Brand Design", "Digital Marketing"]

export function Portfolio() {
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "200px 0px 0px 0px" });
  const isMobile = useIsMobile();

  const filteredItems = activeCategory === "Alle"
    ? portfolioItems
    : portfolioItems.filter(item => item.categories.includes(activeCategory));

  const displayedItems = filteredItems;

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="arbeid" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-black">Vårt Arbeid</span>
          </h2>
          <p className="text-xl text-gray-800 max-w-2xl mx-auto mb-8">
            Utvalgte prosjekter som viser vår kreativitet og ekspertise
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                type="button"
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full transition-all ${
                  activeCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          animate={{ height: isExpanded ? "auto" : isMobile ? 2300 : 1300 }}
          initial={false}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="relative overflow-hidden"
        >
          <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr pt-8 pb-8 px-4">
            {displayedItems.map((item, index) => (
              <motion.div
                key={item.id || index}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full"
              >
                <Link to={`/arbeid/${createSlug(item.title)}`} state={{ from: 'home' }} className="h-full block">
                  <Card
                    className="group glass-card smooth-transition hover:scale-105 hover:-translate-y-2 border-0 overflow-hidden cursor-pointer h-full flex flex-col"
                  >
                <div className="relative overflow-hidden">
                  <div className="w-full h-64 bg-[#0d0d0d] flex items-center justify-center">
                    {item.image && item.image !== "/api/placeholder/600/400" ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : item.launchingSoon ? (
                      <span className="text-white font-bold text-xl tracking-widest uppercase">Kommer snart</span>
                    ) : (
                      <div className="text-6xl opacity-20">🎨</div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <ExternalLink className="w-6 h-6 text-primary" />
                  </div>
                </div>
                
                <CardContent className="p-6 flex-grow">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags?.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center flex-wrap gap-2">
                    <h3 className="text-xl font-semibold text-white break-words">{item.title}</h3>
                    {(item.title === "Pomo-Do" || item.launchingSoon) && (
                      <Badge className="bg-primary/10 text-primary border-primary/20 border whitespace-nowrap shrink-0">Lanseres snart</Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm mt-2">
                    {item.description}
                    <span className="text-primary font-semibold ml-1">Les mer</span>
                  </p>
                </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          {!isExpanded && (
            <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white via-white/80 to-transparent z-10 pointer-events-none" />
          )}
        </motion.div>
        <div className="text-center mt-16">
            <Button 
              size="lg" 
              className="hero-glow smooth-transition hover:scale-105"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Se Mindre" : "Se Mer"}
            </Button>
        </div>
      </div>
    </section>
  )
}
